import { useEffect, useState } from 'react';
import { monEspaceApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { C } from '../../theme/palette';

/** Statuts du serveur, dans les mots de l'écran des livraisons. */
const STATUTS = {
  A_LIVRER: 'À livrer',
  EN_ROUTE: 'En route',
  LIVRE: 'Livrée',
  PARTIEL: 'Partielle',
};

const couleur = (statut) =>
  statut === 'LIVRE' ? C.teal : statut === 'PARTIEL' ? C.purple : C.amber;

const fmtQ = (q) => String(q ?? '').replace(/\.0+$/, '');

/**
 * Onglet « 🚚 Livraison » de l'employé d'un magasin.
 *
 * Les tournées à effectuer — client, adresse, articles, statut — viennent de
 * l'espace de l'employé, sans prix : les bons de livraison du bureau lui
 * restent fermés. Sur place, il coche ce qu'il dépose ; un article décoché
 * reste « manquant », et « ✓ Valider la livraison » passe le bon à Livrée ou
 * Partielle, avec le jour et son nom.
 *
 * @param {object} props propriétés de l'onglet
 * @param {(texte: string) => void} [props.setFlash] message bref
 * @returns {import('react').ReactElement} l'onglet
 */
export function MesLivraisons({ setFlash }) {
  const [bons, setBons] = useState(null);
  const [erreur, setErreur] = useState('');
  // Lignes decochees, par bon : elles restent a livrer.
  const [manque, setManque] = useState({});
  const flash = (t) => setFlash && setFlash(t);

  useEffect(() => {
    let actif = true;
    monEspaceApi
      .livraisons()
      .then((liste) => {
        if (!actif) return;
        setBons(liste || []);
        const depart = {};
        (liste || []).forEach((b) => {
          depart[b.id] = (b.lignes || [])
            .filter((l) => l.etat && l.etat !== 'livre')
            .map((l) => l.id);
        });
        setManque(depart);
      })
      .catch((e) => actif && setErreur(e?.message || 'Les livraisons n’ont pas pu être lues.'));
    return () => {
      actif = false;
    };
  }, []);

  const bascule = (bonId, ligneId) =>
    setManque((m) => {
      const liste = m[bonId] || [];
      return {
        ...m,
        [bonId]: liste.includes(ligneId) ? liste.filter((x) => x !== ligneId) : [...liste, ligneId],
      };
    });

  const valide = async (bon) => {
    const manquantes = manque[bon.id] || [];
    const total = (bon.lignes || []).length;
    if (total > 0 && manquantes.length >= total) {
      flash('Aucun article coché — coche au moins un article livré.');
      return;
    }
    try {
      const relu = await monEspaceApi.valideLivraison(bon.id, manquantes);
      setBons((p) => p.map((b) => (b.id === bon.id ? relu : b)));
      flash(
        manquantes.length > 0
          ? '✓ Livraison partielle — ' + manquantes.length + ' article(s) manquant(s) à relivrer'
          : '✓ Livraison complète effectuée',
      );
    } catch (e) {
      flash('⚠ ' + (e?.message || 'La livraison n’a pas pu être validée.'));
    }
  };

  const itineraire = (bon) => {
    try {
      window.open(
        'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(bon.adresse || ''),
        '_blank',
      );
    } catch {
      // Fenetre bloquee : l'adresse reste lisible a l'ecran.
    }
  };

  return (
    <Card>
      <Bar
        icon="🚚"
        title="Mes livraisons"
        color={C.amber}
        sub="Tournées à effectuer (chantier / client)"
      />
      <div style={{ padding: 13 }}>
        {erreur && (
          <div role="alert" style={{ fontSize: 11, color: C.red, marginBottom: 8 }}>
            {erreur}
          </div>
        )}
        {bons === null && !erreur && (
          <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', padding: '20px 0' }}>
            Chargement…
          </div>
        )}
        {bons && bons.length === 0 && (
          <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', padding: '20px 0' }}>
            Aucune livraison à effectuer pour le moment.
            <div style={{ fontSize: 10, marginTop: 6 }}>
              Le bureau prépare les bons de livraison, ils apparaîtront ici.
            </div>
          </div>
        )}
        {(bons || []).map((b) => {
          const ouvert = b.statut !== 'LIVRE';
          const manquantes = manque[b.id] || [];
          return (
            <div
              key={b.id}
              style={{
                border: '1px solid ' + C.border,
                borderRadius: 11,
                padding: '11px 12px',
                marginBottom: 9,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>{b.clientNom || 'Client'}</div>
                  <div style={{ fontSize: 11, color: C.grey, marginTop: 1 }}>
                    <span translate="no">📍 {b.adresse || '—'}</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                    {b.numero} · {(b.lignes || []).length} article(s)
                  </div>
                </div>
                <Pill label={STATUTS[b.statut] || b.statut} color={couleur(b.statut)} size={8} />
              </div>
              {(b.lignes || []).length > 0 && (
                <div
                  style={{
                    background: C.navyXL,
                    borderRadius: 8,
                    padding: '8px 9px',
                    marginBottom: 8,
                  }}
                >
                  {ouvert && (
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>
                      Coche ce que tu livres. Décoche un article manquant — il restera à livrer.
                    </div>
                  )}
                  {b.lignes.map((x) => {
                    const livre = !manquantes.includes(x.id);
                    return (
                      <div
                        key={x.id}
                        role={ouvert ? 'checkbox' : undefined}
                        aria-checked={ouvert ? livre : undefined}
                        aria-label={x.designation}
                        onClick={ouvert ? () => bascule(b.id, x.id) : undefined}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                          padding: '5px 0',
                          cursor: ouvert ? 'pointer' : 'default',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {ouvert && (
                            <span
                              style={{
                                width: 19,
                                height: 19,
                                borderRadius: 5,
                                border: '1.5px solid ' + (livre ? C.green : C.muted),
                                background: livre ? C.green : 'transparent',
                                color: '#fff',
                                fontSize: 12,
                                lineHeight: '17px',
                                textAlign: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {livre ? '✓' : ''}
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: 11,
                              color: livre ? C.grey : C.muted,
                              textDecoration: livre ? 'none' : 'line-through',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {x.designation}
                          </span>
                        </div>
                        <b
                          style={{
                            fontSize: 10,
                            color: livre ? C.grey : C.amber,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {livre ? fmtQ(x.quantite) + ' ' + (x.unite || '') : 'manquant'}
                        </b>
                      </div>
                    );
                  })}
                </div>
              )}
              {!ouvert && b.livrePar && (
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>
                  Livrée par {b.livrePar}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <Btn
                    label="📍 Itinéraire"
                    sm
                    ghost
                    color={C.navy}
                    full
                    onClick={() => itineraire(b)}
                  />
                </div>
                {ouvert && (
                  <div style={{ flex: 1 }}>
                    <Btn
                      label="✓ Valider la livraison"
                      sm
                      color={C.green}
                      full
                      onClick={() => valide(b)}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ fontSize: 10, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
          💡 Tu vois tes tournées, l&apos;adresse du chantier ou du client, tu ouvres
          l&apos;itinéraire et tu marques « livré » une fois sur place.
        </div>
      </div>
    </Card>
  );
}
